import type { CapitalGlobalProposalStepProps } from '@/features/proposals/components/capital-global/capital-global-proposal-step.types';
import { Button } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, FileText } from 'lucide-react';

type Props = Pick<
  CapitalGlobalProposalStepProps,
  'isViewMode' | 'canGoBack' | 'onBack'
> & {
  canGoToUploadStep?: boolean;
  onGoToUploadStep?: () => void;
  onGeneratePdf: () => void;
  onPhysicalSignature: () => void;
  onDigitalSignature: () => void;
  isGeneratePdfPending: boolean;
  canGeneratePdfInViewMode?: boolean;
  isTransmitted?: boolean;
  shouldHideSignatureActions?: boolean;
  isPhysicalSignaturePending?: boolean;
  isCreateSignaturePending?: boolean;
  isPhysicalSignatureBlocked?: boolean;
  physicalSignatureBlockedReason?: string;
  isDigitalSignatureDisabled?: boolean;
  digitalSignatureBlockedReason?: string;
  isGeneratePdfBlocked?: boolean;
  generatePdfBlockedReason?: string;
};

export function CapitalGlobalProposalResumeFooter({
  isViewMode,
  canGoBack,
  onBack,
  canGoToUploadStep = false,
  onGoToUploadStep,
  onGeneratePdf,
  onPhysicalSignature,
  onDigitalSignature,
  isGeneratePdfPending,
  canGeneratePdfInViewMode = false,
  isTransmitted = false,
  shouldHideSignatureActions = false,
  isPhysicalSignaturePending = false,
  isCreateSignaturePending = false,
  isPhysicalSignatureBlocked = false,
  physicalSignatureBlockedReason,
  isDigitalSignatureDisabled = false,
  digitalSignatureBlockedReason,
  isGeneratePdfBlocked = false,
  generatePdfBlockedReason,
}: Props) {
  const navigate = useNavigate();

  return (
    <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
      <Button
        type='button'
        variant='ghost'
        size='lg'
        className='flex items-center gap-2'
        onClick={onBack}
        disabled={isViewMode || !canGoBack}
      >
        <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
        Voltar
      </Button>

      <div className='flex flex-wrap items-center justify-end gap-2'>
        <Button
          type='button'
          size='md'
          className='flex items-center gap-2 rounded-xl bg-alba-green-500 px-8 py-2 text-alba-white-500 hover:bg-alba-green-600'
          onClick={onGeneratePdf}
          disabled={
            isGeneratePdfPending ||
            isGeneratePdfBlocked ||
            (isViewMode && !canGeneratePdfInViewMode)
          }
          title={generatePdfBlockedReason}
          loading={isGeneratePdfPending}
        >
          <FileText className='h-5 w-5' strokeWidth={1.6} />
          Gerar PDF
        </Button>
        {!shouldHideSignatureActions && (
          <>
            <Button
              type='button'
              size='md'
              className='rounded-xl bg-alba-green-500 px-8 py-2 text-alba-white-500 hover:bg-alba-green-600'
              onClick={onPhysicalSignature}
              disabled={
                isViewMode ||
                isGeneratePdfPending ||
                isPhysicalSignatureBlocked
              }
              loading={isPhysicalSignaturePending}
              title={physicalSignatureBlockedReason}
            >
              Assinatura Física
            </Button>
            <Button
              type='button'
              variant='primary'
              size='md'
              className='rounded-xl px-8 py-2'
              onClick={onDigitalSignature}
              disabled={
                isViewMode ||
                isGeneratePdfPending ||
                isDigitalSignatureDisabled ||
                isCreateSignaturePending
              }
              loading={isCreateSignaturePending}
              title={digitalSignatureBlockedReason}
            >
              Assinatura Digital
            </Button>
          </>
        )}
        <Button
          type='button'
          variant='primary'
          size='md'
          className='rounded-xl px-8 py-2'
          onClick={() => navigate({ to: '/proposals' })}
        >
          Sair
        </Button>
        {!isTransmitted && canGoToUploadStep && onGoToUploadStep && (
          <Button
            type='button'
            variant='primary'
            size='md'
            className='flex items-center gap-2 rounded-xl px-8 py-2'
            onClick={onGoToUploadStep}
          >
            Importar
            <ArrowRight className='h-6 w-6' strokeWidth={2} />
          </Button>
        )}
      </div>
    </div>
  );
}
